import { FormEvent, useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Activity,
  ArrowRight,
  Bell,
  Brain,
  Briefcase,
  History,
  HeartHandshake,
  LayoutDashboard,
  LineChart,
  Radar,
  Settings,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";
import { Link, useNavigate } from "react-router";
import { useSEO } from "../lib/seo";

type ContactForm = {
  name: string;
  email: string;
  message: string;
};

export default function InvestorLanding() {
  useSEO({
    title: "TENSION Investor Landing",
    description:
      "TENSION turns human interaction into real-time measurable intelligence with RTI and predictive relationship dynamics.",
    canonical: "/investors",
    ogType: "website",
    twitterCard: "summary_large_image",
  });

  const reduceMotion = useReducedMotion();
  const navigate = useNavigate();
  const [form, setForm] = useState<ContactForm>({
    name: "",
    email: "",
    message: "",
  });

  const fadeUp = useMemo(
    () => ({
      initial: reduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 },
      whileInView: { opacity: 1, y: 0 },
      viewport: { once: true, margin: "-80px" },
      transition: { duration: reduceMotion ? 0 : 0.55, ease: [0.22, 1, 0.36, 1] as const },
    }),
    [reduceMotion],
  );

  const handleSubmitFounderContact = (e: FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Investor Inquiry — ${form.name || "TENSION Investor"}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\n\nMessage:\n${form.message}`,
    );
    window.location.href = `mailto:hello@tensionlabs.net?subject=${subject}&body=${body}`;
  };

  const dashboardStagger = {
    hidden: { opacity: 0, y: 12 },
    show: (idx: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: reduceMotion ? 0 : 0.45,
        delay: reduceMotion ? 0 : 0.08 * idx,
        ease: [0.22, 1, 0.36, 1] as const,
      },
    }),
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#05050A] text-white">
      <NetworkBackground reduceMotion={!!reduceMotion} />

      <main className="relative z-10 px-4 pb-12 pt-8 sm:px-6 sm:pt-10 lg:pb-16 lg:pt-12">
        <section className="mx-auto w-full max-w-[1320px]">
          <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <TensionLogo />
            <InvestorBadge />
          </div>

          <motion.p {...fadeUp} className="text-lg font-semibold uppercase tracking-[0.08em] text-white/78 md:text-[34px] md:tracking-[0.02em]">
            THE FIRST RELATIONSHIP INTELLIGENCE PLATFORM.
          </motion.p>
          <motion.h1
            {...fadeUp}
            className="mt-2 bg-[linear-gradient(90deg,#FF2D8D_0%,#FF4FD8_34%,#7B61FF_66%,#4DA6FF_100%)] bg-clip-text text-[44px] font-black uppercase leading-[0.98] text-transparent sm:text-[58px] lg:text-[74px] xl:text-[88px]"
          >
            HUMAN INTERACTION, FINALLY UNDERSTOOD.
          </motion.h1>
          <motion.p {...fadeUp} className="mt-5 max-w-[980px] text-base leading-[1.7] text-[#A7A7B8] sm:text-lg">
            TENSION uses AI to decode human dynamics in real-time and over time—turning conversations into actionable intelligence with RTI:
            Real-Time Interaction Intelligence + Relationship Tension Index.
          </motion.p>
          <motion.p {...fadeUp} className="mt-4 text-2xl font-semibold tracking-tight text-white sm:text-3xl">
            The Intelligence Layer for Human Interaction
          </motion.p>

          <motion.div
            initial={reduceMotion ? false : "hidden"}
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="mt-8 overflow-hidden rounded-[26px] border border-[rgba(139,92,246,0.35)] bg-[rgba(255,255,255,0.06)] p-3 shadow-[0_28px_90px_rgba(0,0,0,0.55),0_0_40px_rgba(123,97,255,0.2)] backdrop-blur-2xl sm:p-4"
          >
            <div className="grid gap-3 lg:grid-cols-[220px_1fr_320px]">
              <motion.aside
                variants={dashboardStagger}
                custom={0}
                className="rounded-2xl border border-white/10 bg-[#0B0B12]/90 p-4"
              >
                <p className="bg-[linear-gradient(90deg,#7B61FF,#FF4FD8,#4DA6FF)] bg-clip-text text-xl font-bold tracking-[0.16em] text-transparent">
                  TENSION
                </p>
                <div className="mt-5 space-y-2">
                  {[
                    [Activity, "Live Session"],
                    [LayoutDashboard, "Overview"],
                    [History, "History"],
                    [Users, "Relationships"],
                    [Brain, "Insights"],
                    [Bell, "Alerts"],
                    [Settings, "Settings"],
                  ].map(([Icon, label], idx) => {
                    const TypedIcon = Icon as typeof Brain;
                    const active = idx === 0;
                    return (
                      <div
                        key={label as string}
                        className={`flex items-center gap-2 rounded-xl px-2.5 py-2 text-sm ${
                          active ? "bg-[rgba(123,97,255,0.18)] text-white" : "text-[#A7A7B8]"
                        }`}
                      >
                        <TypedIcon className="h-4 w-4" />
                        <span>{label as string}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-5 rounded-2xl border border-white/10 bg-[#111522] p-3">
                  <p className="text-xs text-[#A7A7B8]">Overall Relationship Health</p>
                  <MiniGauge value={72} label="Good" />
                </div>
              </motion.aside>

              <motion.div
                variants={dashboardStagger}
                custom={1}
                className="rounded-2xl border border-white/10 bg-[#070A15]/92 p-4"
              >
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-xl font-semibold sm:text-2xl">Relationship Tension Index (RTI)</h3>
                  <div className="flex items-center gap-2 rounded-full bg-white/5 px-3 py-1 text-xs text-white/85">
                    <motion.span
                      animate={reduceMotion ? undefined : { opacity: [0.4, 1, 0.4], scale: [1, 1.12, 1] }}
                      transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
                      className="h-2.5 w-2.5 rounded-full bg-[#FF3D3D] shadow-[0_0_12px_rgba(255,61,61,0.75)]"
                    />
                    LIVE SESSION
                  </div>
                </div>

                <RTIGauge value={68} reduceMotion={!!reduceMotion} />

                <div className="mt-4 grid gap-3 sm:grid-cols-4">
                  <MetricCard label="Speaking Pace" value="↑ 18%" />
                  <MetricCard label="Interruptions" value="4" />
                  <MetricCard label="Sentiment" value="-0.48" />
                  <MetricCard label="Engagement" value="62%" />
                </div>

                <div className="mt-4 rounded-2xl border border-white/10 bg-[#0D1222] p-3">
                  <p className="mb-2 text-sm text-white/80">Tension Over Time</p>
                  <TensionGraph reduceMotion={!!reduceMotion} />
                </div>
              </motion.div>

              <motion.div
                variants={dashboardStagger}
                custom={2}
                className="space-y-3 rounded-2xl border border-white/10 bg-[#090C18]/90 p-3"
              >
                <InsightCard
                  title="TONE SHIFT DETECTED"
                  subtitle="Passive → Defensive"
                  value=""
                  color="#FF4D8D"
                  sparkline="M4 42 C 22 28, 34 44, 54 36 C 70 30, 86 40, 106 20 C 126 8, 142 30, 160 10"
                />
                <InsightCard
                  title="EMOTIONAL SIGNAL"
                  subtitle="Friction Increasing"
                  value=""
                  color="#FF8A1F"
                  sparkline="M4 40 C 24 36, 44 44, 64 32 C 82 22, 98 38, 118 20 C 134 8, 148 26, 160 16"
                />
                <InsightCard
                  title="PREDICTION"
                  subtitle="Conflict Probability"
                  value="72%"
                  color="#7B61FF"
                  sparkline="M4 44 C 20 34, 36 44, 54 34 C 76 22, 92 18, 112 30 C 132 40, 146 24, 160 14"
                />
                <div className="rounded-2xl border border-white/10 bg-[rgba(255,255,255,0.04)] p-4">
                  <p className="text-xs font-semibold text-white/80">RECOMMENDED ACTION</p>
                  <p className="mt-3 text-sm leading-relaxed text-[#A7A7B8]">
                    Acknowledge concerns, slow the pace, and validate their perspective.
                  </p>
                  <button className="mt-4 inline-flex w-full items-center justify-center rounded-xl bg-[linear-gradient(90deg,#4DA6FF,#7B61FF,#FF4FD8)] px-4 py-2.5 text-sm font-semibold shadow-[0_10px_20px_rgba(123,97,255,0.35)] transition-transform duration-250">
                    View Suggestions
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>

          <section id="what-is-tension" className="mt-8 grid gap-4 lg:grid-cols-3">
            <InfoPanel title="WHAT IS TENSION?">
              TENSION is the first platform to translate human interaction into real-time, measurable, and actionable intelligence.
              We are building the intelligence layer for human connection in the age of AI.
            </InfoPanel>
            <InfoPanel title="POWERED BY RTI">
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-semibold text-white">Real-Time Interaction Intelligence</p>
                  <p>Live insight into tone, intent, emotional signals, and behavioral changes.</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Relationship Tension Index</p>
                  <p>Patent-pending algorithm quantifying tension, alignment, trust, and behavioral patterns over time.</p>
                </div>
              </div>
            </InfoPanel>
            <InfoPanel title="WHY THIS MATTERS">
              <ul className="space-y-2">
                <li>✓ Every high-stakes outcome is driven by human interaction.</li>
                <li>✓ No system exists to measure and predict relationship dynamics—until now.</li>
                <li>✓ TENSION unlocks the final frontier of AI: Human Behavior.</li>
              </ul>
            </InfoPanel>
          </section>

          <section className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <InvestorValueCard
              icon={Users}
              title="Massive Market"
              body="Personal, professional, and enterprise opportunities across every industry."
            />
            <InvestorValueCard
              icon={Sparkles}
              title="First-of-its-Kind"
              body="Creating a new category: Relationship Intelligence."
            />
            <InvestorValueCard
              icon={Radar}
              title="Proprietary Edge"
              body="Patent-pending RTI algorithm + data network effects."
            />
            <InvestorValueCard
              icon={Briefcase}
              title="Foundational Infrastructure"
              body="Positioned to become the operating system for human interaction intelligence."
            />
          </section>

          <section id="investors" className="mt-4 grid gap-4 lg:grid-cols-[1fr_1fr]">
            <motion.div
              {...fadeUp}
              className="rounded-2xl border border-[rgba(139,92,246,0.35)] bg-[rgba(255,255,255,0.06)] p-6 shadow-[0_20px_50px_rgba(0,0,0,0.45)] backdrop-blur-xl"
            >
              <h2 className="text-3xl font-black uppercase leading-tight tracking-wide md:text-4xl">
                PRE-REVENUE. CATEGORY-DEFINING. BUILT FOR SCALE.
              </h2>
              <p className="mt-4 max-w-[620px] text-[#A7A7B8]">
                We are opening a limited number of conversations with investors who see what’s next—before the world does.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/business-inquiries?inquiryType=investor")}
                  className="rounded-xl bg-[linear-gradient(90deg,#4DA6FF,#7B61FF,#FF2D8D)] px-5 py-3 text-sm font-semibold shadow-[0_10px_24px_rgba(123,97,255,0.35)] transition-transform duration-250"
                >
                  Request Investor Conversation
                </button>
                <Link
                  to="/investors-full-page"
                  className="inline-flex items-center rounded-xl border border-white/25 bg-white/5 px-5 py-3 text-sm font-semibold transition-all duration-250 hover:bg-white/10"
                >
                  View Product Vision
                </Link>
              </div>
            </motion.div>

            <motion.form
              {...fadeUp}
              onSubmit={handleSubmitFounderContact}
              className="rounded-2xl border border-[rgba(139,92,246,0.35)] bg-[rgba(255,255,255,0.06)] p-6 shadow-[0_20px_50px_rgba(0,0,0,0.45)] backdrop-blur-xl"
            >
              <p className="text-xs uppercase tracking-[0.18em] text-white/70">If you believe the future belongs to those who understand people better, let&apos;s connect.</p>
              <div className="mt-4 space-y-3">
                <input
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Your name"
                  required
                  className="w-full rounded-xl border border-white/15 bg-[#0D1222] px-4 py-3 text-white outline-none transition-colors placeholder:text-white/45 focus:border-[#7B61FF]"
                />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="Your email"
                  required
                  className="w-full rounded-xl border border-white/15 bg-[#0D1222] px-4 py-3 text-white outline-none transition-colors placeholder:text-white/45 focus:border-[#7B61FF]"
                />
                <textarea
                  value={form.message}
                  onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
                  placeholder="Investor context / message"
                  required
                  rows={4}
                  className="w-full resize-none rounded-xl border border-white/15 bg-[#0D1222] px-4 py-3 text-white outline-none transition-colors placeholder:text-white/45 focus:border-[#7B61FF]"
                />
                <button
                  type="submit"
                  className="inline-flex w-full items-center justify-center rounded-xl bg-[linear-gradient(90deg,#4DA6FF,#7B61FF,#FF2D8D)] px-6 py-3 font-semibold text-white shadow-[0_10px_24px_rgba(123,97,255,0.35)] transition-transform duration-250"
                >
                  Connect with Founder
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              </div>
            </motion.form>
          </section>
        </section>
      </main>

      <footer className="relative z-10 border-t border-white/10 px-4 py-10 text-center sm:px-6">
        <p className="text-base font-semibold tracking-[0.28em] text-[#A7A7B8] sm:text-lg">
          BETTER INTERACTIONS. BETTER OUTCOMES. BETTER WORLD.
        </p>
      </footer>
    </div>
  );
}

function TensionLogo() {
  return (
    <div>
      <p className="bg-[linear-gradient(90deg,#7B61FF_0%,#FF4FD8_42%,#4DA6FF_100%)] bg-clip-text text-[42px] font-black uppercase leading-none tracking-[0.08em] text-transparent sm:text-[56px]">
        TENSION
      </p>
      <p className="mt-1 text-xs uppercase tracking-[0.2em] text-[#A7A7B8] sm:text-sm">
        Relationship Intelligence Platform
      </p>
    </div>
  );
}

function InvestorBadge() {
  return (
    <div className="rounded-xl border border-white/20 bg-[rgba(255,255,255,0.08)] px-4 py-2 text-xs font-semibold uppercase tracking-[0.1em] text-white/90 shadow-[0_0_24px_rgba(123,97,255,0.25)]">
      PRE-REVENUE | INVESTOR OPPORTUNITY
    </div>
  );
}

function NetworkBackground({ reduceMotion }: { reduceMotion: boolean }) {
  const nodes = [
    { x: "6%", y: "12%", size: 4 },
    { x: "20%", y: "8%", size: 5 },
    { x: "34%", y: "14%", size: 3 },
    { x: "52%", y: "6%", size: 4 },
    { x: "73%", y: "12%", size: 5 },
    { x: "88%", y: "18%", size: 4 },
    { x: "14%", y: "58%", size: 5 },
    { x: "28%", y: "48%", size: 4 },
    { x: "42%", y: "62%", size: 3 },
    { x: "63%", y: "54%", size: 5 },
    { x: "80%", y: "58%", size: 4 },
    { x: "92%", y: "72%", size: 3 },
  ];

  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_15%,rgba(77,166,255,0.14),transparent_35%),radial-gradient(circle_at_75%_25%,rgba(123,97,255,0.18),transparent_42%),radial-gradient(circle_at_85%_75%,rgba(255,45,141,0.16),transparent_45%)]" />

      <svg className="absolute inset-0 h-full w-full opacity-35" viewBox="0 0 1000 700" preserveAspectRatio="none" aria-hidden>
        <defs>
          <linearGradient id="networkLine" x1="0" x2="1">
            <stop offset="0%" stopColor="#4DA6FF" stopOpacity="0.2" />
            <stop offset="50%" stopColor="#7B61FF" stopOpacity="0.32" />
            <stop offset="100%" stopColor="#FF4FD8" stopOpacity="0.2" />
          </linearGradient>
        </defs>
        <path d="M40 120 C 220 40, 340 180, 520 120 C 700 60, 830 160, 980 120" stroke="url(#networkLine)" strokeWidth="1.2" fill="none" />
        <path d="M80 440 C 260 360, 400 520, 560 460 C 740 390, 860 520, 980 470" stroke="url(#networkLine)" strokeWidth="1.2" fill="none" />
        <path d="M140 250 C 260 180, 400 310, 600 280 C 760 260, 860 360, 980 330" stroke="url(#networkLine)" strokeWidth="1.2" fill="none" />
      </svg>

      {nodes.map((node, idx) => (
        <motion.span
          key={`${node.x}-${node.y}-${idx}`}
          className="absolute rounded-full bg-[#7B61FF] shadow-[0_0_18px_rgba(123,97,255,0.8)]"
          style={{ left: node.x, top: node.y, width: node.size, height: node.size }}
          animate={
            reduceMotion
              ? undefined
              : {
                  opacity: [0.4, 1, 0.4],
                  scale: [1, 1.35, 1],
                  y: [0, idx % 2 === 0 ? -6 : 6, 0],
                }
          }
          transition={{
            repeat: Infinity,
            duration: 3.2 + (idx % 4) * 0.45,
            ease: "easeInOut",
            delay: idx * 0.08,
          }}
        />
      ))}
    </div>
  );
}

function MiniGauge({ value, label }: { value: number; label: string }) {
  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (value / 100) * circumference;

  return (
    <div className="mt-3 flex items-center gap-3">
      <svg width="82" height="82" className="shrink-0">
        <circle cx="41" cy="41" r={radius} stroke="#222736" strokeWidth="8" fill="none" />
        <circle
          cx="41"
          cy="41"
          r={radius}
          stroke="url(#miniGauge)"
          strokeWidth="8"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform="rotate(-90 41 41)"
        />
        <defs>
          <linearGradient id="miniGauge" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#30E070" />
            <stop offset="55%" stopColor="#FF8A1F" />
            <stop offset="100%" stopColor="#FF2D8D" />
          </linearGradient>
        </defs>
        <text x="41" y="47" textAnchor="middle" className="fill-white text-[18px] font-semibold">
          {value}%
        </text>
      </svg>
      <p className="text-sm text-[#30E070]">{label}</p>
    </div>
  );
}

function RTIGauge({ value, reduceMotion }: { value: number; reduceMotion: boolean }) {
  const radius = 140;
  const circumference = 2 * Math.PI * radius;
  const maxArc = circumference * 0.75;
  const dashOffset = maxArc - (value / 100) * maxArc;
  const angle = -135 + (value / 100) * 270;

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0D1222] p-4">
      <div className="relative mx-auto flex w-full max-w-[560px] items-center justify-center">
        <svg viewBox="0 0 360 280" className="w-full">
          <defs>
            <linearGradient id="rtiArc" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#30E070" />
              <stop offset="40%" stopColor="#FFD84D" />
              <stop offset="70%" stopColor="#FF8A1F" />
              <stop offset="100%" stopColor="#FF2D4D" />
            </linearGradient>
          </defs>
          <g transform="translate(180,180)">
            <path
              d={describeArc(0, 0, radius, -135, 135)}
              fill="none"
              stroke="#21263A"
              strokeWidth="18"
              strokeLinecap="round"
            />
            <motion.path
              d={describeArc(0, 0, radius, -135, 135)}
              fill="none"
              stroke="url(#rtiArc)"
              strokeWidth="18"
              strokeLinecap="round"
              strokeDasharray={maxArc}
              strokeDashoffset={maxArc}
              animate={{ strokeDashoffset: dashOffset }}
              transition={{ duration: reduceMotion ? 0 : 1.8, ease: "easeOut" }}
            />
            <motion.g
              initial={{ rotate: -135 }}
              animate={{ rotate: angle }}
              transition={{ duration: reduceMotion ? 0 : 1.6, ease: "easeOut" }}
            >
              <line x1="0" y1="0" x2={radius - 4} y2="0" stroke="#FFDEDE" strokeWidth="3" strokeLinecap="round" />
              <circle cx="0" cy="0" r="7" fill="#fff" />
            </motion.g>
          </g>
          <text x="180" y="148" textAnchor="middle" className="fill-white text-[62px] font-semibold">
            {value}
          </text>
          <text x="180" y="180" textAnchor="middle" className="fill-[#FF5D6B] text-[19px] font-semibold">
            Elevated Tension Detected
          </text>
          <text x="180" y="206" textAnchor="middle" className="fill-[#A7A7B8] text-[14px]">
            Proceed with awareness
          </text>
          <text x="32" y="236" className="fill-[#A7A7B8] text-[14px]">0</text>
          <text x="314" y="236" className="fill-[#A7A7B8] text-[14px]">100</text>
        </svg>
      </div>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center">
      <p className="text-[11px] uppercase tracking-[0.08em] text-[#A7A7B8]">{label}</p>
      <p className="mt-1 text-lg font-semibold text-white">{value}</p>
    </div>
  );
}

function TensionGraph({ reduceMotion }: { reduceMotion: boolean }) {
  return (
    <svg viewBox="0 0 640 220" className="h-[160px] w-full sm:h-[180px]">
      <defs>
        <linearGradient id="tensionLine" x1="0" x2="1">
          <stop offset="0%" stopColor="#30E070" />
          <stop offset="40%" stopColor="#FF8A1F" />
          <stop offset="75%" stopColor="#FF2D8D" />
          <stop offset="100%" stopColor="#FF4FD8" />
        </linearGradient>
      </defs>
      <g stroke="rgba(255,255,255,0.12)" strokeWidth="1">
        <line x1="30" y1="24" x2="30" y2="196" />
        <line x1="30" y1="196" x2="612" y2="196" />
      </g>
      <motion.path
        d="M36 178 C 92 160, 130 140, 168 156 C 208 170, 242 122, 284 126 C 328 130, 360 168, 404 126 C 448 84, 490 126, 538 84 C 564 62, 588 90, 612 52"
        fill="none"
        stroke="url(#tensionLine)"
        strokeWidth="5"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: reduceMotion ? 0 : 2.1, ease: "easeOut" }}
      />
      <circle cx="170" cy="155" r="6" fill="#FF8A1F" />
      <circle cx="542" cy="82" r="6" fill="#FF4FD8" />
      <text x="120" y="114" fill="#A7A7B8" fontSize="12">Interrupt detected</text>
      <text x="496" y="42" fill="#A7A7B8" fontSize="12">Tone escalation</text>
    </svg>
  );
}

function InsightCard({
  title,
  subtitle,
  value,
  color,
  sparkline,
}: {
  title: string;
  subtitle: string;
  value: string;
  color: string;
  sparkline: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[rgba(255,255,255,0.04)] p-4 transition-all duration-250 hover:shadow-[0_0_26px_rgba(123,97,255,0.2)]">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-white/80">{title}</p>
      <p className="mt-1 text-sm text-[#A7A7B8]">{subtitle}</p>
      {value ? <p className="mt-1 text-[34px] font-bold text-white">{value}</p> : null}
      <svg viewBox="0 0 166 48" className="mt-2 h-10 w-full">
        <path d={sparkline} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" />
      </svg>
    </div>
  );
}

function InvestorValueCard({
  icon,
  title,
  body,
}: {
  icon: typeof Brain;
  title: string;
  body: string;
}) {
  const Icon = icon;
  return (
    <motion.article
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="rounded-2xl border border-[rgba(139,92,246,0.35)] bg-[rgba(255,255,255,0.06)] p-5 shadow-[0_16px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl"
    >
      <Icon className="h-7 w-7 text-[#7B61FF]" />
      <h3 className="mt-3 text-lg font-semibold">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-[#A7A7B8]">{body}</p>
    </motion.article>
  );
}

function InfoPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <motion.div
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="rounded-2xl border border-[rgba(139,92,246,0.35)] bg-[rgba(255,255,255,0.06)] p-5 shadow-[0_16px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl"
    >
      <h3 className="text-xl font-semibold tracking-tight text-white">{title}</h3>
      <div className="mt-3 text-sm leading-relaxed text-[#A7A7B8]">{children}</div>
    </motion.div>
  );
}

function polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

function describeArc(x: number, y: number, radius: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  return ["M", start.x, start.y, "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y].join(" ");
}
